<template>
  <LayoutWrapper>
    <div
      class="prose prose-neutral flex max-w-none flex-col bg-light-0 dark:prose-invert dark:bg-dark-0 md:flex-row"
    >
      <div
        class="h-screen overflow-y-auto bg-light-0 px-8 dark:bg-dark-0 md:grow md:basis-0"
      >
        <OrderOverview
          v-model:selected-order="selectedOrder"
          :closed-orders="closedOrders"
          :grouped-open-orders="groupedOpenOrders"
          :is-loading="isLoading"
          :last-updated-timestamp="lastUpdatedTimestamp"
          @refresh-orders="fetchOrders"
        />
      </div>
      <div
        class="fixed -bottom-40 z-10 flex w-full justify-center rounded-3xl bg-light-1 align-middle transition-all dark:bg-dark-1 md:relative md:bottom-0 md:right-0 md:mt-0 md:rounded-none"
        :class="[selectedOrder ? 'h-screen w-full md:w-1/2' : 'h-0  md:w-0']"
      >
        <OrderDetails
          v-if="selectedOrder"
          v-model:selected-order="selectedOrder"
          @go-to-order="goToOrder($event)"
        />
      </div>
    </div>
  </LayoutWrapper>
</template>

<script setup lang="ts">
import { onMounted, computed } from 'vue';
import OrderOverview from '@/components/OrderManagement/OrderOverview.vue';
import OrderDetails from '@/components/OrderManagement/OrderDetails.vue';
import { useAppStore } from '@/store/app/AppStore';
import { WebEvents } from '@/native/plugins/WebEvents';
import { default as envConfig } from '@/config/runtimeConfig';
import { installRelease } from '@/utils/liveUpdater';
import { fetchLatestReleases } from '@/api/mobile/AppInfoApi';
import LayoutWrapper from '@/components/LayoutWrapper.vue';
import { useLayoutStore } from '@/store/layout/layoutStore';
import { useOrderStore } from '@/store/order/orderStore';
import { useEntitiesStore } from '@/store/entities/entitiesStore';
import { useOrderOverview } from '@/composables/useOrderOverview';
import router from '@/router';

const appStore = useAppStore();
const layoutStore = useLayoutStore();
const orderStore = useOrderStore();

const {
  selectedOrder,
  lastUpdatedTimestamp,
  closedOrders,
  groupedOpenOrders,
  updateTimestamp,
  autoSelectFirstOrder,
  cleanUpOldData,
} = useOrderOverview();

const getOrdersFromApi = () => orderStore.fetchAllOrders();

const fetchOrders = async () => {
  updateTimestamp();

  layoutStore.setLoadingState(true);
  await getOrdersFromApi().finally(() => {
    layoutStore.setLoadingState(false);
  });
  autoSelectFirstOrder();
  await cleanUpOldData();
  layoutStore.setLoadingState(false);
};

const isLoading = computed(() => {
  return layoutStore.isLoading;
});

const goToOrder = async () => {
  const entitiesStore = useEntitiesStore();
  const orderStore = useOrderStore();
  const layoutStore = useLayoutStore();

  if (!selectedOrder.value) return;

  const startedOrder = await orderStore.startOrder(selectedOrder.value.id);
  if (!startedOrder) {
    console.error('Failed to start order in OrderManagement', {
      orderId: selectedOrder.value.id,
    });
    return;
  }

  await entitiesStore
    .setActiveBusinessEntityById(selectedOrder.value.businessEntityId)
    .then(() => {
      router.push('/gebaeudeuebersicht');
      if (entitiesStore.activeBusinessEntity) {
        layoutStore.changeCurrentNode(entitiesStore.activeBusinessEntity);
      }
    })
    .catch((error) => {
      console.error(error);
    });
};

onMounted(async () => {
  WebEvents.onLoggedIn();
  appStore.shouldOpenChangelogDialog().then((shouldOpen) => {
    if (shouldOpen) {
      appStore.toggleChangelogDialog();
    }
  });

  try {
    await fetchOrders();
  } catch (e) {
    layoutStore.setLoadingState(false);
  }

  if (appStore.isLiveUpdateEnabled()) {
    const payload = appStore.getLiveUpdatePayload();

    if (payload['environments'].includes(envConfig.sentryEnv)) {
      fetchLatestReleases().then((data) => {
        if (data.length > 0) {
          const latestVersion = data[0];
          console.debug('Latest version' + latestVersion);
          installRelease(latestVersion).then(() => () => {
            console.debug('Live Updater Done');
          });
        }
      });
    }
  }
});
</script>
